/* eslint-disable react/jsx-fragments */
/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { useState } from 'react';
import { jsx } from '@emotion/react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import { observer } from 'mobx-react-lite';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Icon, IconButton, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';

/**
 * The internal dependencies
 */
import { UpdateChangeDtoStatusEnum } from '@codefluegel/zeta-change-typescript-client';
import { useTranslation } from 'react-i18next';
import { InfoOutline } from '@mui/icons-material';
import { useStorex } from '../../store/UIStore';
import { useChangeStore } from '../../store/changeStore';
import {
  STATUSES_FOR_CALC,
  EXCLUDED_STATUSES_FOR_RESOURCES,
  STATUSES_FOR_EXTERN,
  STATUSES_FOR_IMPLEMENTATION,
} from '../../constants/statusesForChecking';
import useMedia from '../../hooks/useMedia';
import MainMenuList from '../Lists/MainMenuList';
import TutorialModal from '../Modals/TutorialModal';
import {
  getChangeLinkByStatus,
  getTutorialStepbyStatus,
} from '../../utils/utils';
import { TaskCardLocalizationList } from '../../constants/changes';
import TaskCardIconMap from '../../utils/changes';
import { checkIfRejected } from '../../utils/permissionHelper';

const drawerWidth = 236;

const SidebarHeader = observer(() => {
  const { change } = useChangeStore();
  const { t } = useTranslation();
  const TaskCardLocalization = TaskCardLocalizationList();
  const [openTutorial, setOpenTutorial] = useState(false);

  const isRejected = checkIfRejected(change);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        pt: 1,
        position: 'relative',
        borderBottom: '1px solid #e0e0e0', // Add a subtle divider
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">
          {change?.status === UpdateChangeDtoStatusEnum.Draft || !change
            ? t('change.newChange')
            : `${change?.changeNo || change.commonProject?.number}`}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setOpenTutorial(true)}
          sx={{ ml: 1 }}
        >
          <InfoOutline color="primary" fontSize="small" />
        </IconButton>
      </Box>

      <Typography
        sx={{ wordBreak: 'break-all', fontWeight: 'bold' }}
        variant="body2"
      >
        {change?.title}
      </Typography>

      {change?.status && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ wordBreak: 'break-all' }} variant="body2">
            {TaskCardLocalization[change?.status as UpdateChangeDtoStatusEnum]}
          </Typography>
          <Icon>
            {TaskCardIconMap[change?.status as UpdateChangeDtoStatusEnum]}
          </Icon>
        </Box>
      )}
      {isRejected && change?.rejectionComment && (
        <Typography>{change?.rejectionComment}</Typography>
      )}

      <TutorialModal
        open={openTutorial}
        step={getTutorialStepbyStatus(change)}
        handleClose={() => setOpenTutorial(false)}
      />
    </Box>
  );
});

const SidebarDrawer = observer(() => {
  const store = useStorex();
  const { change } = useChangeStore();
  const { pathname } = useLocation();
  const { changeId } = useParams();
  const isMobile = useMedia('sm');

  const highlightString = change && getChangeLinkByStatus(change.status);

  // Condense the visibility logic for better readability
  const menuVisibility = {
    isCalcVisible: change?.status && STATUSES_FOR_CALC.includes(change.status),
    isResourcesVisible:
      change?.status &&
      !EXCLUDED_STATUSES_FOR_RESOURCES.includes(change.status),
    isExternVisible:
      change?.status && STATUSES_FOR_EXTERN.includes(change.status),
    isImplementationVisible:
      change?.status && STATUSES_FOR_IMPLEMENTATION.includes(change.status),
  };

  if (isMobile) {
    // Mobile view: Simple menu list without the persistent drawer
    return (
      <MainMenuList
        changeId={changeId ?? ''}
        pathname={pathname}
        highlightString={highlightString || ''}
        isCalcVisible={menuVisibility.isCalcVisible}
        isResourcesVisible={menuVisibility.isResourcesVisible}
        isExternVisible={menuVisibility.isExternVisible}
        isImplementationVisible={menuVisibility.isImplementationVisible}
      />
    );
  }

  // Desktop view: Persistent drawer
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Drawer
        PaperProps={{ elevation: 3 }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: '45px',
            height: 'calc(100% - 45px)',
            backgroundColor: 'white',
            border: 'none',
          },
        }}
        variant="persistent"
        anchor="left"
        open={store.sidebar}
      >
        <Toolbar sx={{ minHeight: 'unset', padding: 0 }}>
          <SidebarHeader />
        </Toolbar>
        <MainMenuList
          changeId={changeId ?? ''}
          pathname={pathname}
          highlightString={highlightString || ''}
          isCalcVisible={menuVisibility.isCalcVisible}
          isResourcesVisible={menuVisibility.isResourcesVisible}
          isExternVisible={menuVisibility.isExternVisible}
          isImplementationVisible={menuVisibility.isImplementationVisible}
        />
        {/* New collapse button within the drawer */}
        <IconButton
          onClick={store.toggleSidebar}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
            transform: store.sidebar ? 'rotate(0deg)' : 'rotate(180deg)',
          }}
        >
          {store.sidebar ? (
            <ChevronLeftIcon color="primary" />
          ) : (
            <ChevronRightIcon color="primary" />
          )}
        </IconButton>
      </Drawer>
    </Box>
  );
});

export default SidebarDrawer;
